import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  FormButtonStyled,
  HeadingStyled,
  LabelStyled,
  TextStyled,
} from "../../styled/Elements";
import { Form, Modal } from "react-bootstrap";
import ValidatedInput from "../../common/inputFields/ValidatedInput";
import { useTranslation } from "react-i18next";
import { SelectionDropdown } from "../../styled/FormElements";
import MultipleImageUpload from "../../../pages/web/profile/components/MultipleImagesUpload";
import ValidationController from "../../common/inputFields/ValidationController";
import PropertiesApi from "../../../services/api/propertiesApi";
import { toast } from "react-toastify";
import { Controller, useForm } from "react-hook-form";
import FormSkeleton from "../../profile/FormSkeleton";
import VisitorApi from "../../../services/api/visitorApi";
import CustomRadioButton from "../../common/radioButtons/CustomRadio";
import { countWords, formatAmountForDisplay } from "../../../utils";
import MultipleVideoUpload from "../../../pages/web/profile/components/MultipleVideoUpload";
import { uploadPropertyMediaFiles } from "../../../services/propertyMediaUpload";

const EditPropertyModal = (props) => {
  const {
    id: { id, hashId },
  } = props;

  const citiesJson = localStorage.getItem("citiesList");
  const cities = citiesJson ? JSON.parse(citiesJson) : [];
  const platformJson = localStorage.getItem("platforms");
  const platforms = citiesJson ? JSON.parse(platformJson) : [];
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [selectedImages, setSelectedImages] = useState({});
  const [deletedImages, setDeletedImages] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [isPlatform, setIsPlatform] = useState(null);
  const [propertyTypes, setPropertyTypes] = useState(null);
  const [advertiserTypes, setAdvertiserTypes] = useState([]);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadingType, setUploadingType] = useState(null);
  const [progress, setProgress] = useState(0);
  const [resetTrigger, setResetTrigger] = useState(0);

  const mediaTypeCategoryMap = {
    exterior_images: { type: 1, category: 1 },
    interior_images: { type: 1, category: 2 },
    exterior_videos: { type: 2, category: 1 },
    interior_videos: { type: 2, category: 2 },
  };
  const [uploadedFilesMap, setUploadedFilesMap] = useState({
    interior_images: new Set(),
    exterior_images: new Set(),
    interior_videos: new Set(),
    exterior_videos: new Set(),
  });

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm();

  const uploadSessionIdRef = useRef(null);
  const abortControllersRef = useRef({});
  const isUploadingRef = useRef(false);
  const shouldAbortRef = useRef(false);

  const cityId = watch("city_id");
  const propertyType = watch("listing_property_type_id");

  const cleanupUploads = () => {
    console.log("Cleaning up uploads...");

    // Set abort flag
    shouldAbortRef.current = true;

    // Abort all ongoing requests
    Object.values(abortControllersRef.current).forEach((controller) => {
      try {
        controller.abort();
      } catch (error) {
        console.log("Error aborting controller:", error);
      }
    });

    // Clear controllers
    abortControllersRef.current = {};

    // Reset upload state
    setIsUploading(false);
    setUploadingType(null);
    setProgress(0);
    isUploadingRef.current = false;

    // Reset uploaded files map
    setUploadedFilesMap({
      interior_images: new Set(),
      exterior_images: new Set(),
      interior_videos: new Set(),
      exterior_videos: new Set(),
    });

    // Invalidate upload session
    uploadSessionIdRef.current = null;
  };
  const handleClose = () => {
    setResetTrigger((prev) => prev + 1);
    reset(); // Reset form fields
    setSelectedImages({});
    setDeletedImages([]);
    props.onHide(); // Close modal
    props.setId({
      id: null,
      hashId: null,
    });
  };
  useEffect(() => {
    if (props.show) {
      shouldAbortRef.current = false;
    }
  }, [props.show]);

  useEffect(() => {
    if (props?.show) setResetTrigger(0);
  }, [props?.show, resetTrigger]);

  const handleFileChange = async (files, onChange, name) => {
    if (!files || !files.length) return;

    if (shouldAbortRef.current) {
      return;
    }

    const currentFieldUploads = new Set(uploadedFilesMap[name]);
    const filesToUpload = Array.from(files).filter(
      (file) => !currentFieldUploads.has(file.name)
    );
    if (filesToUpload.length === 0) {
      return;
    }

    setIsUploading(true);
    setUploadingType(name);

    try {
      const uploadedPaths = await uploadPropertyMediaFiles(filesToUpload, {
        onProgress: setProgress,
        shouldAbort: () => shouldAbortRef.current,
      });

      if (!shouldAbortRef.current && uploadedPaths.length > 0) {
        filesToUpload.forEach((file) => currentFieldUploads.add(file.name));
        const field = watch(name);
        onChange(field ? [...field, ...uploadedPaths] : uploadedPaths);
        setUploadedFilesMap((prev) => ({
          ...prev,
          [name]: currentFieldUploads,
        }));
      }
    } catch (err) {
      if (!shouldAbortRef.current) {
        console.error("File upload failed:", err);
        toast.error(t("something_went_wrong") || err?.message);
      }
    } finally {
      setIsUploading(false);
      setUploadingType(null);
      setProgress(0);
    }
  };

  const getAdvertiserTypes = async () => {
    try {
      const response = await PropertiesApi.getListingStatuses();
      const types = response?.data?.advertiser_types;
      if (Array.isArray(types)) {
        setAdvertiserTypes(
          types.map((item) => ({
            ...item,
            label: item?.label || item?.key,
            value: item?.key,
          }))
        );
      }
    } catch (error) {
      setAdvertiserTypes([]);
    }
  };

  const getPropertyTypes = async () => {
    try {
      const response = await PropertiesApi.getListingPropertyTypes();
      if (response?.data) {
        setPropertyTypes(
          response?.data &&
            response?.data
              ?.filter((item) => item?.id != 15)
              ?.map((item) => {
                return {
                  ...item,
                  label:
                    language === "en" ? item?.en_name : item?.ar_name,
                  value: item?.id,
                };
              })
        );
      }
    } catch (error) {
      console.log(error.message);
      setPropertyTypes([]);
    }
  };

  const getDistrict = async (cityId) => {
    try {
      const { data, code } = await VisitorApi.getDistricts(cityId);
      if (code === 200 && data && Array.isArray(data) && data.length > 0) {
        setDistricts(
          data.map((dst) => ({
            ...dst,
            value: dst.id,
            label: language === "en" ? dst?.name_en : dst?.name_ar,
          }))
        );
      } else {
        setDistricts(
          cities?.filter((city) => parseInt(city?.value) === parseInt(cityId))
        );
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (cityId) {
      getDistrict(cityId);
    }
  }, [cityId, language]);

  useEffect(() => {
    getPropertyTypes();
    getAdvertiserTypes();
  }, [language]);

  const getPropertyDetails = async () => {
    try {
      setIsFetching(true);
      const { data, code } = await PropertiesApi.getSingleProperty(hashId);

      reset({
        listing_type: Number(data?.listing_type?.id),
        city_id: Number(data?.city_id),
        neighborhood: Number(data?.neighborhood_data?.id),
        listing_property_type_id: Number(data?.listing_property_type?.id),
        other_prop_type_en: data?.listing_property_type?.other_prop_type_en,
        other_prop_type_ar: data?.listing_property_type?.other_prop_type_ar,
        property_status: Number(data?.property_status?.id),
        description_en: data?.description_en,
        description_ar: data?.description_ar,
        ad_number: data?.ad_number,
        advertiser_type: data?.advertiser_type?.key || "",
        contact_phone: data?.contact_phone || "",
        price: data?.price || "",
        // exterior_images:
        //   data?.media?.exterior_images?.length > 0
        //     ? data?.media?.exterior_images?.map((img) => img)
        //     : [],
      });
      getDistrict(Number(data?.city_id));

      const transformedMedia = {
        exterior_images:
          data?.media?.exterior_images?.map((url, i) => ({
            id: data?.media_detail?.exterior_images?.[i]?.id, // get ID here
            image: url,
          })) || [],
        interior_images:
          data?.media?.interior_images?.map((url, i) => ({
            id: data?.media_detail?.interior_images?.[i]?.id,
            image: url,
          })) || [],
        exterior_videos:
          data?.media?.exterior_videos?.map((url, i) => ({
            id: data?.media_detail?.exterior_videos?.[i]?.id,
            video: url,
          })) || [],
        interior_videos:
          data?.media?.interior_videos?.map((url, i) => ({
            id: data?.media_detail?.interior_videos?.[i]?.id,
            video: url,
          })) || [],
      };
      setSelectedImages(transformedMedia);
    } catch (error) {
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (hashId) {
      getPropertyDetails();
    }
  }, [hashId]);

  const deleteListingImages = async () => {
    try {
      const { code, message } = await PropertiesApi.removePropertyImages({
        delete_property_images: deletedImages,
      });
      if (code === 100 || code === 204) {
        toast.error(message);
      }
    } catch (error) {
    } finally {
      setDeletedImages([]);
    }
  };

  const handleEditProperty = async (dataObj) => {
    setLoading(true);
    try {
      const media = [];
      const formData = new FormData();
      formData.append("action", "update");
      formData.append("property_id", id);
      Object.entries(dataObj).forEach(([key, value]) => {
        if (
          [
            "exterior_images",
            "interior_images",
            "exterior_videos",
            "interior_videos",
          ].includes(key)
        ) {
          const config = mediaTypeCategoryMap[key];
          if (Array.isArray(value)) {
            value.forEach((file) => {
              media.push({
                file,
                type: config.type,
                category: config.category,
              });
            });
          }
        } else {
          formData.append(key, value);
        }
      });
      if (deletedImages.length > 0) {
        deletedImages.forEach((id) => formData.append("delete_media[]", id));
      }

      media.forEach((mediaItem, index) => {
        formData.append(`media[${index}][file]`, mediaItem.file);
        formData.append(`media[${index}][type]`, mediaItem.type);
        formData.append(`media[${index}][category]`, mediaItem.category);
      });
      const { code, message } = await PropertiesApi.saveProperty(formData);
      if (code === 200) {
        toast.success(t("property_updated_successfully"));
        props?.getPropertiesList();
        handleClose();
      }
      if (code === 100 || code === 204) {
        toast.error(message);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={props?.show}
      onHide={handleClose}
      size="md"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Form onSubmit={handleSubmit(handleEditProperty)}>
        <Modal.Header className="">
          <Modal.Title>
            <HeadingStyled size="24px" className="">
              {t("EditListing")}
            </HeadingStyled>
          </Modal.Title>
        </Modal.Header>
        {isFetching ? (
          <FormSkeleton />
        ) : (
          <Modal.Body>
            {/* <Box className="d-flex flex-column align-items-center py-3 px-2"> */}
            <Box className="formEleContainer">
              <Box className="w-100">
                <LabelStyled className="labelForm">
                  {t("listing_type")}
                  {/* <span className="staric">*</span> */}
                </LabelStyled>
                <Box className="w-100">
                  <ValidationController
                    name={"listing_type"}
                    control={control}
                    errors={errors}
                    validationConfig={{
                      required: `fieldRequired`,
                    }}
                    renderer={({ field }) => (
                      <Box className="d-flex gap-2">
                        <Box className="w-100">
                          <CustomRadioButton
                            name="listing_type"
                            label={t("for_rent")}
                            value={1}
                            checked={1 == field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                          />
                        </Box>
                        <Box className="w-100">
                          <CustomRadioButton
                            name="listing_type"
                            label={t("for_sale")}
                            value={2}
                            checked={2 == field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                          />
                        </Box>
                      </Box>
                    )}
                  />
                </Box>
              </Box>
            </Box>
            <Box className="formEleContainer">
              <Box className="w-100">
                <LabelStyled className="labelForm">
                  {t("property_type")}
                  {/* <span className="staric">*</span> */}
                </LabelStyled>
                <ValidationController
                  name={"listing_property_type_id"}
                  control={control}
                  errors={errors}
                  validationConfig={{
                    required: `fieldRequired`,
                  }}
                  renderer={({ field }) => (
                    <Box className="mt-2">
                      <SelectionDropdown
                        name={"listing_property_type_id"}
                        placeholder={t("choose_type")}
                        value={field.value}
                        options={propertyTypes || []}
                        onChange={(e) => {
                          setValue("other_prop_type_en", "");
                          setValue("other_prop_type_ar", "");
                          field.onChange(e.target.value);
                        }}
                      />
                    </Box>
                  )}
                />

                {propertyType && Number(propertyType) === 7 && (
                  <>
                    <ValidatedInput
                      name="other_prop_type_en"
                      label={"other_en"}
                      type="input"
                      fieldType="text"
                      placeholder={"Other EN"}
                      className={"w-100 mb-0 combined flex-wrap p-0"}
                      register={register}
                      errors={errors}
                      validationRules={{
                        required: true,
                      }}
                    />
                    <ValidatedInput
                      name="other_prop_type_ar"
                      label={"other_ar"}
                      type="input"
                      fieldType="text"
                      placeholder={"أخرى (بالعربية)"}
                      className={"w-100 mb-0 combined flex-wrap p-0"}
                      register={register}
                      errors={errors}
                      validationRules={{
                        required: true,
                      }}
                    />
                  </>
                )}
              </Box>
            </Box>
            <Box className="formEleContainer">
              <Box className="w-100">
                <LabelStyled className="labelForm">
                  {t("AdvertiserType")}
                </LabelStyled>
                <ValidationController
                  name={"advertiser_type"}
                  control={control}
                  errors={errors}
                  validationConfig={{}}
                  renderer={({ field }) => (
                    <Box className="mt-2">
                      <SelectionDropdown
                        name={"advertiser_type"}
                        placeholder={t("SelectAdvertiserType")}
                        value={field.value}
                        options={advertiserTypes || []}
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                    </Box>
                  )}
                />
              </Box>
            </Box>
            <Box className="formEleContainer">
              <Box className="w-100">
                <LabelStyled className="labelForm">
                  {t("property_status")}
                  {/* <span className="staric">*</span> */}
                </LabelStyled>
                <Box className="w-100">
                  <ValidationController
                    name={"property_status"}
                    control={control}
                    errors={errors}
                    validationConfig={{
                      required: `fieldRequired`,
                    }}
                    renderer={({ field }) => (
                      <Box className="d-flex gap-2">
                        <Box className="w-100">
                          <CustomRadioButton
                            name="property_status"
                            label={t("ready")}
                            value={1}
                            checked={1 == field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                          />
                        </Box>
                        <Box className="w-100">
                          <CustomRadioButton
                            name="property_status"
                            label={t("off_plan")}
                            value={2}
                            checked={2 == field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                          />
                        </Box>
                      </Box>
                    )}
                  />
                </Box>
              </Box>
            </Box>
            <Box className="d-flex gap-2 flex-sm-nowrap flex-wrap">
              <Box className="formEleContainer w-100">
                <LabelStyled className="labelForm">{t("City")}</LabelStyled>
                <ValidationController
                  name={"city_id"}
                  control={control}
                  errors={errors}
                  validationConfig={{
                    required: `fieldRequired`,
                  }}
                  renderer={({ field }) => (
                    <SelectionDropdown
                      name={field.name}
                      placeholder={t("City")}
                      options={cities}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
              </Box>
              <Box className="formEleContainer w-100">
                <LabelStyled className="labelForm">
                  {t("Neighbourhood")}
                </LabelStyled>
                <ValidationController
                  name={"neighborhood"}
                  control={control}
                  errors={errors}
                  validationConfig={{
                    required: `fieldRequired`,
                  }}
                  renderer={({ field }) => (
                    <SelectionDropdown
                      name={field.name}
                      placeholder={t("Neighbourhood")}
                      options={districts}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
              </Box>
            </Box>

            <Box className="d-flex gap-2 flex-sm-nowrap flex-wrap">
              <Controller
                name="price"
                control={control}
                rules={{ required: true }}
                render={({
                  field: { onChange, value, ref },
                  fieldState: { error },
                }) => (
                  <ValidatedInput
                    name="price"
                    label={"price_sar"}
                    type="input"
                    fieldType="text" // must be text to allow formatting
                    placeholder={"price_sar"}
                    className={"w-100 mb-0 combined flex-wrap p-0"}
                    value={value ? formatAmountForDisplay(value) : ""}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      const raw = inputValue.replace(/,/g, "");

                      // Allow empty, decimal point only, or valid decimal numbers with up to 2 decimal places
                      if (
                        raw === "" ||
                        raw === "." ||
                        /^\d*\.?\d{0,2}$/.test(raw)
                      ) {
                        onChange(raw);
                      }
                    }}
                    errors={{ price: error }}
                  />
                )}
              />
              <ValidatedInput
                name="ad_number"
                label={"AdNumber"}
                type="input"
                fieldType="number"
                placeholder={"AdNumber"}
                className={"w-100 mb-0 combined flex-wrap p-0"}
                register={register}
                errors={errors}
                validationRules={{
                  required: true,
                  validate: (value) => {
                    const str = value.toString();
                    return (
                      /^7/.test(str) || t("validation_must_start_with_seven")
                    );
                  },
                }}
              />
            </Box>

            <ValidatedInput
              name="contact_phone"
              label={"confirm_contact_number"}
              type="input"
              fieldType="tel"
              placeholder={"confirm_contact_number"}
              className={"w-100 mb-0 combined flex-wrap p-0"}
              register={register}
              errors={errors}
              validationRules={{}}
            />

            <ValidatedInput
              name="description_en"
              label={"description_en"}
              type="textarea"
              fieldType="textarea"
              placeholder={"Description EN (Maximum 200 words)"}
              className={"w-100 mb-0 combined flex-wrap p-0"}
              register={register}
              errors={errors}
              validationRules={{
                required: true,
                validate: (value) =>
                  countWords(value) <= 200 || "max_200_words_allowed",
              }}
            />
            <TextStyled color="var(--text3)" size="14px">
              {t("recommended_num_of_rooms")}
            </TextStyled>
            <ValidatedInput
              name="description_ar"
              label={"description_ar"}
              type="textarea"
              fieldType="textarea"
              placeholder={"الوصف (بالإنجليزية) - بحد أقصى 200 كلمة"}
              className={"w-100 mb-0 combined flex-wrap p-0"}
              register={register}
              errors={errors}
              validationRules={{
                required: true,
                validate: (value) =>
                  countWords(value) <= 200 || "max_200_words_allowed",
              }}
            />
            <TextStyled color="var(--text3)" size="14px">
              {t("recommended_num_of_rooms")}
            </TextStyled>

            {/* <Box className="formEleContainer">
            <Box>
              <LabelStyled className="labelForm">
                {t("AdPlatform")}
              </LabelStyled>
              <Box className="w-100 d-flex gap-2">
                <Box>
                  <CustomRadioButton
                    name="is_platform"
                    label={t("none")}
                    value={0}
                    checked={isPlatform == 0}
                    onChange={(e) => {
                      setValue("ad_platform", []);
                      setIsPlatform(e.target.value);
                    }}
                  />
                </Box>
                <Box>
                  <CustomRadioButton
                    name="is_platform"
                    label={t("AdPlatform")}
                    value={1}
                    checked={isPlatform == 1}
                    onChange={(e) => setIsPlatform(e.target.value)}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          {isPlatform == 1 && (
            <Box className="formEleContainer">
              <ValidationController
                name={"ad_platform"}
                control={control}
                errors={errors}
                validationConfig={{
                  required: `fieldRequired`,
                }}
                renderer={({ field }) => (
                  <MultiSelectionDropdown
                    name={field.name}
                    placeholder={t("select_platforms")}
                    options={platforms}
                    value={field.value || []}
                    onChange={(selected) => field.onChange(selected)}
                  />
                )}
              />
            </Box>
          )} */}

            <Box className="formEleContainer w-100">
              <LabelStyled className="labelForm w-100">
                {t("exterior_images")}
              </LabelStyled>
              <ValidationController
                name={"exterior_images"}
                control={control}
                errors={errors}
                validationConfig={{
                  required: false,
                  // required: `fieldRequired`,
                }}
                renderer={({ field }) => (
                  <MultipleImageUpload
                    onChange={(files) =>
                      handleFileChange(files, field.onChange, field.name)
                    }
                    setDeletedImages={setDeletedImages}
                    max={20}
                    isLoading={
                      isUploading && uploadingType === "exterior_images"
                    }
                    resetTrigger={resetTrigger}
                    disabled={isUploading}
                    selectedImages={selectedImages?.exterior_images}
                  />
                )}
              />

              <TextStyled color="var(--text3)" size="14px">
                {t("upload_upto_20_img")}
              </TextStyled>
            </Box>
            <Box className="formEleContainer w-100">
              <LabelStyled className="labelForm w-100">
                {t("interior_images")}
              </LabelStyled>
              <ValidationController
                name={"interior_images"}
                control={control}
                errors={errors}
                validationConfig={{
                  required: false,
                  // required: `fieldRequired`,
                }}
                renderer={({ field }) => (
                  <MultipleImageUpload
                    onChange={(files) =>
                      handleFileChange(files, field.onChange, field.name)
                    }
                    setDeletedImages={setDeletedImages}
                    isLoading={
                      isUploading && uploadingType === "interior_images"
                    }
                    resetTrigger={resetTrigger}
                    disabled={isUploading}
                    max={50}
                    selectedImages={selectedImages?.interior_images}
                  />
                )}
              />
              <TextStyled color="var(--text3)" size="14px">
                {t("upload_upto_50_img")}
              </TextStyled>
            </Box>
            <Box className="formEleContainer w-100">
              <LabelStyled className="labelForm w-100">
                {t("exterior_videos")}
              </LabelStyled>
              <ValidationController
                name={"exterior_videos"}
                control={control}
                errors={errors}
                validationConfig={{
                  required: false,
                  // required: `fieldRequired`,
                }}
                renderer={({ field }) => (
                  <MultipleVideoUpload
                    onChange={(files) =>
                      handleFileChange(files, field.onChange, field.name)
                    }
                    setDeletedVideos={setDeletedImages}
                    isLoading={
                      isUploading && uploadingType === "exterior_videos"
                    }
                    resetTrigger={resetTrigger}
                    disabled={isUploading}
                    max={4}
                    selectedVideos={selectedImages?.exterior_videos}
                  />
                )}
              />
              <TextStyled color="var(--text3)" size="14px">
                {t("upload_upto_4_vid")}
              </TextStyled>
            </Box>
            <Box className="formEleContainer w-100">
              <LabelStyled className="labelForm w-100">
                {t("interior_videos")}
              </LabelStyled>
              <ValidationController
                name={"interior_videos"}
                control={control}
                errors={errors}
                validationConfig={{
                  required: false,
                  // required: `fieldRequired`,
                }}
                renderer={({ field }) => (
                  <MultipleVideoUpload
                    onChange={(files) =>
                      handleFileChange(files, field.onChange, field.name)
                    }
                    isLoading={
                      isUploading && uploadingType === "interior_videos"
                    }
                    resetTrigger={resetTrigger}
                    setDeletedVideos={setDeletedImages}
                    disabled={isUploading}
                    max={50}
                    maxDuration={180}
                    selectedVideos={selectedImages?.interior_videos}
                  />
                )}
              />
              <TextStyled color="var(--text3)" size="14px">
                {t("upload_unlimited_vid")}
              </TextStyled>
            </Box>

            {/* </Box> */}
          </Modal.Body>
        )}
        <Modal.Footer className="">
          <FormButtonStyled
            label={t("Cancel")}
            className=""
            clName=""
            bg="transparent"
            shadow="false"
            color="var(--lightGrey)"
            onClick={handleClose}
            border="var(--border)"
            type="button"
          />
          <FormButtonStyled
            label={t("Update")}
            className=""
            clName="mb-2"
            disabled={loading}
            isLoading={loading}
            type="submit"
            bg="var(--themeColorSecondary)"
          />
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditPropertyModal;
